<template>
	<div
		v-measure-width="{
			callback: onInteractionOverlayWidthChange,
			sizes: VIDEO_INTERACTION_OVERLAY_CONFIG
		}"
		class="video-interaction"
		:class="[
			`-${interactionOverlaySize}`,
			{
				'is-touch-mode': touchMode,
				'overlay-is-visible': playerOverlayIsVisible,
			}
		]"
	>
		<div
			ref="backwardIndicator"
			class="video-interaction__indicator -left"
			:class="{
				'is-visible': showBackward,
				'is-transitioning': transitioningBackward,
			}"
		>
			<div class="video-interaction__blades">
				<svg
					v-for="blade in backwardBlades"
					:key="blade.key"
					class="video-interaction__blade"
					:style="blade.style"
					width="24"
					height="24"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 47 47"
				>
					<polygon
						class="st0"
						points="9.8,0.1 46.5,36.8 46.5,46 37.3,46 0.6,9.3 0.6,0.1 "
					/>
				</svg>
			</div>
			<div>{{ skipLabelBackward }}</div>
		</div>
		<div
			v-if="!hidePlayPause"
			class="video-interaction__controls"
			:class="{
				'is-visible-in-touch-mode': playerOverlayIsVisible,
			}"
		>
			<div
				ref="playIndicator"
				class="video-interaction__indicator -center"
				:class="{
					'is-visible': forcePlayVisible || (touchMode ? showPause : showPlay),
					'is-transitioning': transitioningPlay,
				}"
				@click="togglePlayPause"
			>
				<Icon :icon-name="'button-play-filled'" />
			</div>
			<div
				ref="pauseIndicator"
				class="video-interaction__indicator -center"
				:class="{
					'is-visible': touchMode ? showPlay : showPause,
					'is-transitioning': transitioningPause,
				}"
				@click="togglePlayPause"
			>
				<Icon :icon-name="'button-pause-filled'" />
			</div>
		</div>
		<div
			ref="forwardIndicator"
			class="video-interaction__indicator -right"
			:class="{
				'is-visible': showForward,
				'is-transitioning': transitioningForward,
			}"
		>
			<div class="video-interaction__blades">
				<svg
					v-for="blade in forwardBlades"
					:key="blade.key"
					class="video-interaction__blade"
					:style="blade.style"
					width="24"
					height="24"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 47 47"
				>
					<polygon
						class="st0"
						points="9.8,0.1 46.5,36.8 46.5,46 37.3,46 0.6,9.3 0.6,0.1 "
					/>
				</svg>
			</div>
			<div>{{ skipLabelForward }}</div>
		</div>
	</div>
</template>

<script>
import { VIDEO_INTERACTION_OVERLAY_CONFIG } from '@shared/constants/interactionOverlaySizes';
import VMeasureWidth from '@shared/directives/v-measure-width';

const ANIMATION_DURATION = 500;

const generateBlades = (count, keyPrefix = '') => {
	const blades = [];
	for (let i = 0; i < count; i++) {
		blades.push({
			key: `${keyPrefix}-${i}`,
		});
	}
	return blades;
};

export default {
	name:       'VideoInteractionFeedbackOverlay',
	directives: {
		measureWidth: VMeasureWidth,
	},
	props: {
		forcePlayVisible: {
			type:    Boolean,
			default: false,
		},
		isPlaying: {
			type:    Boolean,
			default: false,
		},
		// keeps play/pause button visible
		// Is clickable
		touchMode: {
			type:    Boolean,
			default: false,
		},
		// set to true when the player overlay is visible
		playerOverlayIsVisible: {
			type:    Boolean,
			default: false,
		},
		hidePlayPause: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			showBackward:           false,
			showForward:            false,
			showPlay:               this.touchMode ? this.isPlaying : false,
			showPause:              this.touchMode ? !this.isPlaying : false,
			timeouts:               [],
			skipNumberForward:      0,
			skipNumberBackward:     0,
			transitioningBackward:  false,
			transitioningForward:   false,
			transitioningPlay:      false,
			transitioningPause:     false,
			interactionOverlaySize: 'lg',
			VIDEO_INTERACTION_OVERLAY_CONFIG,
		};
	},
	computed: {
		skipLabelForward() {
			return `${this.skipNumberForward} sec`;
		},
		skipLabelBackward() {
			return `${this.skipNumberBackward} sec`;
		},
		backwardBlades() {
			return generateBlades(3, 'backward');
		},
		forwardBlades() {
			return generateBlades(3, 'forward');
		},
	},
	watch: {
		isPlaying(newVal) {
			if (this.touchMode) {
				this.showPlay = newVal;
				this.showPause = !newVal;
			}
		},
	},
	mounted() {
		this.$refs.backwardIndicator.addEventListener('transitionend', this.handleSkipTransitionEndBackward);
		this.$refs.forwardIndicator.addEventListener('transitionend', this.handleSkipTransitionEndForward);
		if (!this.hidePlayPause) {
			this.$refs.playIndicator.addEventListener('transitionend', this.handlePlayTransitionEnd);
			this.$refs.pauseIndicator.addEventListener('transitionend', this.handlePauseTransitionEnd);
		}
	},
	beforeDestroy() {
		this.$refs.backwardIndicator.removeEventListener('transitionend', this.handleSkipTransitionEndBackward);
		this.$refs.forwardIndicator.removeEventListener('transitionend', this.handleSkipTransitionEndForward);
		if (!this.hidePlayPause) {
			this.$refs.playIndicator.removeEventListener('transitionend', this.handlePlayTransitionEnd);
			this.$refs.pauseIndicator.removeEventListener('transitionend', this.handlePauseTransitionEnd);
		}
	},
	methods: {
		onInteractionOverlayWidthChange(newSize) {
			this.interactionOverlaySize = newSize;
		},
		reset(callback = null, resetPlayPause = true) {
			for (const timeout of this.timeouts) {
				clearTimeout(timeout);
			}
			this.timeouts = [];
			if (resetPlayPause) {
				this.showPlay = false;
				this.showPause = false;
			}
			this.showBackward = false;
			this.showForward = false;
			if (callback) this.$nextTick(callback);
		},
		handlePlay() {
			this.transitioningPlay = true;
			this.reset(() => {
				this.showPlay = true;
				if (this.touchMode) {
					return;
				}
				this.timeouts.push(
					setTimeout(() => {
						this.showPlay = false;
					}, ANIMATION_DURATION)
				);
			});
		},
		handlePause() {
			this.transitioningPause = true;
			this.reset(() => {
				this.showPause = true;
				if (this.touchMode) {
					return;
				}
				this.timeouts.push(
					setTimeout(() => {
						this.showPause = false;
					}, ANIMATION_DURATION)
				);
			});
		},
		handleBackward(number = 10) {
			this.transitioningBackward = true;
			this.skipNumberBackward = this.skipNumberBackward + number;
			this.reset(() => {
				this.showBackward = true;
				this.timeouts.push(
					setTimeout(() => {
						this.showBackward = false;
					}, ANIMATION_DURATION)
				);
			}, !this.touchMode);
		},
		handleForward(number = 10) {
			this.transitioningForward = true;
			this.skipNumberForward = this.skipNumberForward + number;
			this.reset(() => {
				this.showForward = true;
				this.timeouts.push(
					setTimeout(() => {
						this.showForward = false;
					}, ANIMATION_DURATION)
				);
			}, !this.touchMode);
		},
		handleSkipTransitionEndBackward(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningBackward = false;
				this.skipNumberBackward = 0;
			}
		},
		handleSkipTransitionEndForward(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningForward = false;
				this.skipNumberForward = 0;
			}
		},
		handlePlayTransitionEnd(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningPlay = false;
			}
		},
		handlePauseTransitionEnd(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningPause = false;
			}
		},
		togglePlayPause(e) {
			if (this.touchMode) {
				e.stopPropagation();
				this.$emit('toggle-play-pause');
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$indicator-background: rgba(6, 7, 13, 0.4);
$animation-easing: cubic-bezier(0.25, 0, 0.66, 0.99);

@keyframes blade-rotate {
	0% {
		transform: rotate(0deg);
	}

	40% {
		transform: rotate(-180deg);
	}

	100% {
		transform: rotate(-180deg);
	}
}

.video-interaction {
	// &.-sm {}
	--skip-size: 58px;
	--skip-text-size: 10px;
	--blade-size: 9px;
	--play-pause-size: 72px;
	--play-pause-icon-size: 32px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	pointer-events: none;

	&.-md {
		--skip-size: 72px;
		--skip-text-size: 12px;
		--blade-size: 11px;
		--play-pause-size: 96px;
		--play-pause-icon-size: 36px;
	}

	&.-lg {
		--skip-size: 96px;
		--skip-text-size: 14px;
		--blade-size: 17px;
		--play-pause-size: 114px;
		--play-pause-icon-size: 38px;
	}
}

.video-interaction__controls {
	position: relative;
	width: var(--play-pause-size);

	.video-interaction.is-touch-mode & {
		opacity: 0;
		transition: opacity $player-overlay-animation;

		&.is-visible-in-touch-mode {
			opacity: 1;
		}
	}
}

.video-interaction__indicator {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.8em;
	border-radius: 100%;
	width: var(--skip-size);
	height: var(--skip-size);
	white-space: pre;
	font-size: var(--skip-text-size); // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
	line-height: 1;
	color: var(--color-soft-white);
	background-color: $indicator-background; // stylelint-disable-line declaration-property-value-allowed-list
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.3s $animation-easing, visibility 0.3s $animation-easing;
	will-change: opacity;
	transform: translateZ(0); // Force hardware acceleration on iOS

	&.is-visible {
		opacity: 1;
		visibility: visible;
	}

	svg { // stylelint-disable-line selector-max-type
		fill: var(--color-white);
	}

	&.-center {
		position: absolute;
		top: 50%;
		left: 50%;
		justify-content: center;
		width: var(--play-pause-size);
		height: var(--play-pause-size);
		transform: translate3d(-50%, -50%, 0); // Combine with hardware acceleration
		pointer-events: auto;

		> div { // stylelint-disable-line max-nesting-depth, selector-max-type
			width: var(--play-pause-icon-size);
			height: var(--play-pause-icon-size);
		}
	}
}

.video-interaction__blades {
	display: flex;
	gap: 0.3em;

	.video-interaction__indicator.-right & {
		transform: scaleX(-1);
	}
}

.video-interaction__blade {
	width: var(--blade-size);
	height: var(--blade-size);

	.video-interaction__indicator.is-transitioning & {
		animation: blade-rotate 1000ms $animation-easing infinite;  // 500ms animation + 100ms pause = 600ms total

		&:nth-child(1) {
			animation-delay: 0ms;
		}

		&:nth-child(2) {
			animation-delay: 100ms;
		}

		&:nth-child(3) {
			animation-delay: 200ms;
		}
	}
}
</style>
